import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Alert,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { customerSponsorApi, Customer } from '../../api/customerSponsorApi';
import { primaryButtonSx } from '../../theme/tokens';
import { WriteButton } from '../../components/ui';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called with the newly created customer after a successful create. */
  onCreated?: (customer: Customer) => void;
}

/**
 * Create a new platform customer (top of the org hierarchy). Additive only —
 * a fresh customer is referenced by nothing yet, so this is safe even though it
 * writes straight to the live platform (no publish staging like tenants have).
 */
export default function CreateCustomerDialog({ open, onClose, onCreated }: Props) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => { setName(''); setDescription(''); setError(null); setSubmitting(false); };
  const handleClose = () => { if (!submitting) { reset(); onClose(); } };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setError('Customer name is required'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const created = await customerSponsorApi.createCustomer({
        customer_name: trimmed,
        customer_description: description.trim() || undefined,
      });
      await queryClient.invalidateQueries({ queryKey: ['lookup-customers'] });
      onCreated?.(created);
      reset();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to create customer');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Create Customer</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Customer Name *"
            size="small"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
            helperText="Display name for the customer organization"
          />
          <TextField
            label="Description"
            size="small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            helperText="Optional — defaults to the customer name"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none' }}>Cancel</Button>
        <WriteButton ignoreEnvReadOnly onClick={handleSubmit} disabled={submitting || !name.trim()} variant="contained" sx={{ ...primaryButtonSx }}>
          {submitting ? 'Creating…' : 'Create Customer'}
        </WriteButton>
      </DialogActions>
    </Dialog>
  );
}
